import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router";
import { request, truncateAddress, validateBusinessAccountName } from "../../utils";
import Spinner from "../../components/ui/Spinner/Spinner";
import {
  ArrowLeft,
  ChevronDownIcon,
  InfoIcon,
  TriangleAlert,
} from "lucide-react";
import axios from "axios";
import { Modal } from "../../components/ui/modal";
import { useModal } from "../../hooks/useModal";
import { SendingPaymentDetailsModal } from "../../components/PaymentLink/Modals/SendingPaymentDetailsModal";
import { format } from "date-fns";
import Alert from "../../components/ui/alert/Alert";
import { convertAmount } from "../../components/PaymentLink/utils";
import Decimal from "decimal.js";
import { EMAIL_PAYMENT_METHODS, PAYMENT_METHOD, selectMethodTypes } from "../../utils/constant";
import CopyButton from "../../components/ui/button/copyButton";
import { buildPaymentDetails, getBlockchainName, getStatusConfig, normalizeToUsd } from "../../utils/helpers";
import { SelectMethodTypeModal } from "../../components/ui/modal/SelectMethodTypeModal";

type Currency = {
  code: string;
  abbr: string;
  name: string;
  symbol: string;
  iso2: string;
  flag: string;
  bankCodeLabel?: string;
  paymentMethod: {
    bankTransfer: boolean;
    mobileMoney: boolean;
    phoneNumberLength: number;
  };
};

type PaymentDetails = {
  accountName?: string;
  accountNumber?: string;
  providerName?: string;
  paymentMethod: string;
  iso2: string;
  currency: string;
  bankCode?: string;
  network?: string;
  _id?: string;
  walletAddress?: string;
  email?: string;
  phoneNumber?: string;
  holderCity?: string;
  holderStreet?: string;
  holderPostalCode?: string;
};

type PayerDetails = {
  name: string;
  email: string;
};

type PaymentData = {
  paymentRequest: {
    _id: string;
    userId: string;
    amount: number;
    currencyIso2: string;
    paymentDetails: PaymentDetails;
    payerDetails: PayerDetails;
    displayMessage: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    currency: CurrencyInfo;
    orderId?: string;
    expiresAt?: string;
  };
  user: string;
};

type CurrencyInfo = {
  name: string;
  abbr: string;
  symbol: string;
  rate: number;
};

type MarketRateData = {
  fromCurrency: CurrencyInfo;
  toCurrency: CurrencyInfo;
  message: string;
};

type ExchangeRateData = {
  acceptedPaymentMethod: string;
  fromCurrency: {
    abbr: string;
    displayRate: number;
    flag: string;
    name: string;
    symbol: string;
  };
  fromCurrencyIso2: string;
  globalId: string;
  minimumAmount: number;
  paymentDetails: {
    accountName: string;
    accountNumber: string;
    bankCode: string;
    currency: {
      abbr: string;
      bankCodeLabel: string;
      flag: string;
      name: string;
      symbol: string;
    };
    isUssdCode: boolean;
    iso2: string;
    paymentMethod: string;
    providerName: string;
    walletAddress: string;
    network: string;
  };
  payoutPaymentMethod: string;
  rate: number;
  status: string;
  toCurrency: {
    abbr: string;
    displayRate: number;
    flag: string;
    name: string;
    symbol: string;
  };
  toCurrencyIso2: string;
  userGlobalId: string;
  userId: string;
  _id: string;
};

type OrderResponse = {
  _id: string;
  amount: number;
  bdcFee: number;
  bdcId: string;
  buyerCreditPaymentDetails: PaymentDetails;
  buyerCreditPaymentMethod: string;
  buyerDebitPaymentDetails: PaymentDetails;
  buyerDebitPaymentMethod: string;
  buyerGlobalId: string;
  buyerMadePayment: boolean;
  buyerPaymentProofUrl: string | null;
  createdAt: string;
  fee: number;
  fromCurrency: string;
  globalId: string;
  hasBuyerPaymentProof: boolean;
  offerId: string;
  paymentReferenceCode: number;
  paymentRequestId: string;
  rate: number;
  sellerCreditPaymentDetails: PaymentDetails;
  sellerGlobalId: string;
  sellerMadePayment: boolean;
  sellerReceivedPayment: boolean;
  source: string;
  status: "PENDING" | "SUCCESSFUL" | "PROCESSING" | "FAILED";
  toCurrency: string;
  updatedAt: string;
  __v: number;
};

type fetchResponse = {
  _id: string;
  globalId: string;
  offerId: string;
  bdcId: string;
  buyerGlobalId: string;
  sellerGlobalId: string;
  fromCurrency: Currency;
  toCurrency: Currency;
  rate: number;
  fee: number;
  bdcFee: number;
  amount: number;
  status: string;
  buyerDebitPaymentMethod: string;
  buyerCreditPaymentMethod: string;
  buyerCreditPaymentDetails: PaymentDetails;
  buyerDebitPaymentDetails: PaymentDetails;
  sellerCreditPaymentDetails: {
    accountName: string;
    accountNumber: string;
    bankCode: string;
    phoneNumber?: string; 
  holderCity?: string; 
  holderStreet?: string;
  holderPostalCode?: string;
    currency: {
      abbr: string;
      bankCodeLabel: string;
      flag: string;
      name: string;
      symbol: string;
    };
    isUssdCode: boolean;
    iso2: string;
    paymentMethod: string;
    providerName: string;
    walletAddress: string;
    network: string;
    email: string;
  };
  source: string;
  paymentRequestId: string;
  buyerMadePayment: boolean;
  hasBuyerPaymentProof: boolean;
  buyerPaymentProofUrl: string | null;
  sellerMadePayment: boolean;
  sellerReceivedPayment: boolean;
  paymentReferenceCode: number;
  createdAt: string;
  updatedAt: string;
  __v: number;
  value: number;
  requireProofUpload: boolean;
};

type preSignRes = {
  Key: string;
  uploadLink: string;
};

function PaymentLinkPay() {
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [paymentRequestToken, setPaymentRequestToken] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [paymentData, setPaymentData] = useState<
    PaymentData["paymentRequest"] | null
  >(null);
  const [user, setUser] = useState<PaymentData["user"] | null>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [accountNameError, setAccountNameError] = useState("");

  // ---- Currency states
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(
    null
  );
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [currencySearch, setCurrencySearch] = useState("");
  const [isExpired, setIsExpired] = useState(false);

  // ---- From and To currencies
  const [toCurrency, setToCurrency] = useState("");
  const [toCurrencyIso2, setToCurrencyIso2] = useState("");
  const [fromCurrency, setFromCurrency] = useState("");
  const [fromCurrencyIso2, setFromCurrencyIso2] = useState("");

  // ---- Rates and calculations
  const [marketRate, setMarketRate] = useState<MarketRateData | null>(null);
  const [exchangeRate, setExchangeRate] = useState<ExchangeRateData | null>(
    null
  );
  const [calculatedAmount, setCalculatedAmount] = useState<number | null>(null);
  const [displayAmount, setDisplayAmount] = useState<string | null>(null);
  const [orderAmount, setOrderAmount] = useState<number | null>(null);
  const [rateError, setRateError] = useState("");

  // Creating Orders
  const [bdcOfferId, setBdcOfferId] = useState<string | null>(null);
  const [isPayInitOpen, setIsPayInitOpen] = useState(false);
  const [payInitError, setPayInitError] = useState<string | null>(null);
  const [isPayInitLoading, setIsPayInitLoading] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [orderDetails, setOrderDetails] = useState<fetchResponse | null>(null);

  // buyerpaid Confirmation
  const [isBuyerPaidLoading, setIsBuyerPaidLoading] = useState(false);
  const [buyerPaidError, setBuyerPaidError] = useState<string | null>(null);
  const [buyerPaid, setBuyerPaid] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState("");

  // ---- Modals
  const { isOpen, openModal, closeModal } = useModal();

  // ---- Decline
  const [isDeclineOpen, setIsDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineError, setDeclineError] = useState<string | null>(null);
  const [declineSuccess, setDeclineSuccess] = useState(false);

  // ---- handle sending from
  // const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"WALLET" | "BANK_TRANSFER" | "MOBILE_MONEY" | "ZELLE" | "INTERAC" | "STABLE_CURRENCY" | "ACH" | "WIRE">("WALLET");
  const [sendTo, setSendTo] = useState("");
  const [paymentDetails, setPaymentDetails] = useState<any>(null);

  const [showMethodTypeModal, setShowMethodTypeModal] = useState(false);
  const [methodTypeContext, setMethodTypeContext] = useState<"PAY" | "RECEIVE">("PAY");
  const [showPayModal, setShowPayModal] = useState(false);


  useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowCurrencyDropdown(false);
      setCurrencySearch("");
    }
  };
  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  // ---- Handler to open decline modal
  const handleOpenDeclineModal = () => {
    setIsDeclineOpen(true);
    setDeclineError(null);
    setDeclineReason("");
  };

  // ---- Handler to close decline modal
  const closeDeclineModal = () => {
    setIsDeclineOpen(false);
    setDeclineReason("");
    setDeclineError(null);
  };

  // ---- Handler to open pay init modal
  const handleOpenPayInitModal = () => {
    setIsPayInitOpen(true);
    setPayInitError(null);
  };

  // ---- Handler to handle file change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
        "image/webp",
        "image/heic",
      ];

      if (file.size > 5 * 1024 * 1024) {
        setUploadError("File size must be less than 5MB");
        setTimeout(() => setUploadError(""), 4000);
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        setUploadError(
          "Invalid file type. Only JPG, PNG, JPEG and PDF are allowed."
        );
        setTimeout(() => setUploadError(""), 4000);
        return;
      }
      setPaymentProof(file);
    }
  };

  // ---- Handler to close pay init modal
  const closePayInitModal = () => {
    setIsPayInitOpen(false);
    setPayInitError(null);
  };

  // ---- Extract token from the URL and set PaymentData
  useEffect(() => {
    const getTokenFromUrl = () => {
      const params = new URLSearchParams(location.search);
      return params.get("token");
    };

    const tokenFromUrl = getTokenFromUrl();
    if (!tokenFromUrl) {
      setError("Token not found in URL.");
      setLoading(false);
      return;
    }

    setPaymentRequestToken(tokenFromUrl);

    const fetchPaymentLink = async () => {
      try {
        const res = await request.get({
          url: `/payment-link/pay?token=${tokenFromUrl}`,
        });

        if (res.data) {
          const paymentData = res.data as PaymentData;
          const requestedData = paymentData.paymentRequest;
          setPaymentData(requestedData);
          setUser(paymentData.user);
          setToCurrency(requestedData.paymentDetails.currency);
          setToCurrencyIso2(requestedData.currencyIso2);

          const today = Date.now();

          const expiryTime = requestedData.expiresAt
            ? new Date(requestedData.expiresAt).getTime()
            : new Date(requestedData.createdAt).getTime() +
              30 * 24 * 60 * 60 * 1000;

          const hasExpired = expiryTime < today;

          if (hasExpired || requestedData.status === "EXPIRED") {
            setStep(5);
            setIsExpired(true);
            return;
          }

          if (
            requestedData.status === "DECLINED" ||
            requestedData.status === "CANCELLED" ||
            requestedData.status === "PAID"
          ) {
            setStep(5);
          }

          if (
            (requestedData.status === "PROCESSING" ||
              requestedData.status === "PENDING") &&
            requestedData.orderId
          ) {
            try {
              const orderRes = await request.get({
                url: `/order/${requestedData.orderId}/payment-request?token=${tokenFromUrl}`,
                auth: false,
              });

              if (orderRes.success) {
                const fetchedData = orderRes.data as fetchResponse;
                setOrderId(fetchedData._id); // <- store order details
                setOrderDetails(fetchedData); // ✅ store the full object
                setStep(4); // <- proceed to step 4
              }
            } catch (orderError) {
              console.error("Failed to fetch order:", orderError);
            }
          }
        }
      } catch (err: any) {
        console.error("Fetch error:", err);
        const errorData =
          err.error === "TokenExpiredError" ||
          err.error === "JsonWebTokenError";
        if (errorData) {
          setStep(5);
          setIsExpired(true);
          return;
        }
        const fallback =
          err.message || "Something went wrong while fetching payment data";
        setError(fallback);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentLink();
  }, [location]);

  // ---- Fetch supported currencies
  useEffect(() => {
    const controller = new AbortController();

    const fetchCurrencies = async () => {
      if (currencies.length > 0) return;

      setLoadingCurrencies(true);
      setError("");

      try {
        const response = await axios.get(
          `${
            import.meta.env.VITE_BASE_URL
          }/wallet/supported-currencies/all`,
          { signal: controller.signal }
        );

        if (response.data?.success && Array.isArray(response.data.data)) {
          const excluded = ["BUSD"];
          const formattedCurrencies: Currency[] = response.data.data
            .filter((currency: any) => !excluded.includes(currency.abbr)).map(
            (currency: any) => ({
              code: currency.abbr,
              name: currency.name,
              symbol: currency.symbol,
              iso2: currency.iso2,
              flag: currency.flag,
              hasBankList: currency.hasBankList ?? false,
              paymentMethod: {
                bankTransfer: currency.paymentMethod?.bankTransfer || false,
                mobileMoney: currency.paymentMethod?.mobileMoney || false,
                phoneNumberLength: currency.paymentMethod?.phoneNumberLength ?? 10,
              },
            })
          );
          setCurrencies(formattedCurrencies);
        }
      } catch (err: any) {
        if (!axios.isCancel(err)) {
          console.error("Failed to fetch currencies:", err);
          setError("Failed to load. Please try again.");
        }
      } finally {
        setLoadingCurrencies(false);
      }
    };

    fetchCurrencies();

    return () => {
      controller.abort();
    };
  }, [currencies.length]);

  // ---- Fetch market rate when currencies change
  useEffect(() => {
    const fetchMarketRate = async () => {
      if (!fromCurrency || !toCurrency) return;

      const newFrom = normalizeToUsd(fromCurrency);
      const newTo = normalizeToUsd(toCurrency);

      setIsLoading(true);
      setRateError("");
      try {
        const res = await request.get({
          url: `/utility/exchange-rate/from/${newFrom}/to/${newTo}`,
          auth: false,
        });

        if (res.data) {
          const marketRate = res.data as MarketRateData;
          setMarketRate(marketRate);
          setRateError("");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch exchange rate. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    if (step === 2) {
      fetchMarketRate();
    }
  }, [fromCurrency, toCurrency, step]);

  // ---- dynamically calculate estimated amount to receive
  useEffect(() => {
    if (paymentData && marketRate) {
      // Calculate using the rate ratio
      const amountToReceive =
        paymentData.amount *
        (marketRate.fromCurrency.rate / marketRate.toCurrency.rate);
      setCalculatedAmount(Math.ceil(amountToReceive));
    }
  }, [paymentData, marketRate]);

  // ---- Fetch final exchange rate with all parameters using the estimated amount
  useEffect(() => {
    const fetchFinalRate = async () => {
      if (
        !fromCurrency ||
        !toCurrency ||
        !fromCurrencyIso2 ||
        !toCurrencyIso2 ||
        !calculatedAmount
      )
        return;

      setIsLoading(true);
      setRateError("");
      try {
        const res = await request.get({
          url: `/utility/exchange-rate`,
          params: {
            fromCurrency,
            toCurrency,
            fromCurrencyIso2,
            toCurrencyIso2,
            amount: calculatedAmount,
          },
          auth: false,
        });

        if (res?.success && res?.data) {
          const exchangeData = res.data as ExchangeRateData;
          setExchangeRate(exchangeData);
          setBdcOfferId(exchangeData._id);
          setStep(3);
        } else {
          // Show the custom message or fallback
          setRateError(res?.message || "Failed to fetch exchange rate.");
        }
      } catch (err: any) {
        console.error(err);
        setRateError(
          err.response?.data?.message ||
            "Cannot continue with the operation. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (step === 2 && calculatedAmount) {
      fetchFinalRate();
    }
  }, [
    fromCurrency,
    toCurrency,
    fromCurrencyIso2,
    toCurrencyIso2,
    calculatedAmount,
    step,
  ]);

  // ---- Based on picked payment method, get the payment details
  const exchangeRatePaymentDetails = Array.isArray(exchangeRate?.paymentDetails)
    ? exchangeRate.paymentDetails
    : exchangeRate?.paymentDetails
      ? [exchangeRate.paymentDetails]
      : [];

const getSelectedExchangePaymentDetails = () => {
  if (!exchangeRatePaymentDetails.length) return null;

  if (paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY && paymentDetails?.network) {
    const networkMatch = exchangeRatePaymentDetails.find(
      (d) => d.paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY && d.network === paymentDetails.network
    );
    if (networkMatch) {
      return networkMatch;
    }
  }

  const methodMatch = exchangeRatePaymentDetails.find((d) => d.paymentMethod === paymentMethod);
  if (methodMatch) return methodMatch;

  return exchangeRatePaymentDetails[0];
};

const selectedExchangePaymentDetails = getSelectedExchangePaymentDetails();

  // ---- our payment method Selection fallback
  useEffect(() => {
    if (!selectedExchangePaymentDetails && exchangeRatePaymentDetails.length > 0) {
      // Auto-select the first available payment method
      setPaymentMethod(exchangeRatePaymentDetails[0].paymentMethod as any);
    }
  }, [selectedExchangePaymentDetails, exchangeRatePaymentDetails]); 

  // ---- unified USSD amount calculation
const calculateUssdAmount = (value: number, rate: number): string => {
  const amount = new Decimal(value).div(rate);
  const rounded = amount.ceil().toNumber();
  return rounded.toString();
};

// For orderDetails (existing orders):
const ussdAmount = orderDetails?.sellerCreditPaymentDetails?.isUssdCode 
  ? calculateUssdAmount(Number(orderDetails.value), Number(orderDetails.rate))
  : null;

// For selectedExchangePaymentDetails (new orders):
const ussdAmountForSelected = selectedExchangePaymentDetails?.isUssdCode && exchangeRate && paymentData
  ? calculateUssdAmount(paymentData.amount, exchangeRate.rate)
  : null;

  // ---- Calculate amount to send using exchangeRate from the bdc.
  useEffect(() => {
    if (exchangeRate && paymentData && selectedExchangePaymentDetails) {
      const { fromCurrency, toCurrency, rate } = exchangeRate;

      const converted = convertAmount({
        amount: paymentData.amount,
        rate,
        fromDisplayRate: fromCurrency.displayRate,
        toDisplayRate: toCurrency.displayRate,
      });

      setOrderAmount(Number(converted.toFixed(30)));
      if (selectedExchangePaymentDetails?.isUssdCode) {
        const rounded = Math.ceil(converted.toNumber());
        setDisplayAmount(
          rounded.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            useGrouping: false,
          })
        );
      } else {
        const rounded = Math.ceil(converted.toNumber() * 100) / 100;
        setDisplayAmount(
          rounded.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        );
      }
    }
  }, [exchangeRate, paymentData, selectedExchangePaymentDetails]);

  // ---- Handle currency selection
  const handleSelectCurrency = (currency: Currency) => {
    setFromCurrency(currency.code);
    setFromCurrencyIso2(currency.iso2);
    setSelectedCurrency(currency);
    setShowCurrencyDropdown(false);
    setCurrencySearch("");

    if (selectedCurrency?.code !== currency.code) {
      setPaymentDetails(null);
      setSendTo("");
    }
  };

  // ---- Handle next button click
  const handleNext = () => {
    setSelectedCurrency(null);
    setFromCurrency("");
    setFromCurrencyIso2("");
    setMarketRate(null);
    setCalculatedAmount(null);
    setRateError("");
    setStep(2);
    setAccountNameError("");
  };

  // ---- Handle save payment details and build it for various payment methods also name check
const handleSavePaymentDetails = (details: PaymentDetails) => {
  if (!selectedCurrency) return;

  const baseDetails: Partial<PaymentDetails> = {
    accountName: details.accountName,
    accountNumber: details.accountNumber,
    currency: selectedCurrency.code,
    iso2: selectedCurrency.iso2,
  };

  let builtDetails: PaymentDetails;

  if (details.paymentMethod === PAYMENT_METHOD.WECHAT_PAY || details.paymentMethod === PAYMENT_METHOD.ALIPAY) {
    builtDetails = {
      accountName: details.accountName,
      phoneNumber: details.phoneNumber,
      paymentMethod: details.paymentMethod,
      currency: selectedCurrency.code,
      iso2: selectedCurrency.iso2,
    } as PaymentDetails;
  } else if (details.paymentMethod === PAYMENT_METHOD.MOBILE_MONEY) {
    builtDetails = {
      ...baseDetails,
      network: details.providerName,
      paymentMethod: details.paymentMethod,
      providerName: details.providerName,
    } as PaymentDetails;
  } else if (
  details.paymentMethod === PAYMENT_METHOD.BANK_TRANSFER ||
  details.paymentMethod === PAYMENT_METHOD.ACH ||
  details.paymentMethod === PAYMENT_METHOD.WIRE
) {
    builtDetails = {
      ...baseDetails,
      bankCode: details.bankCode || "",
      paymentMethod: details.paymentMethod || "",
      providerName: details.providerName || details.bankCode || "",
      ...((selectedCurrency.iso2 === "GB" || selectedCurrency.code === "EUR") &&
  details.holderCity && details.holderStreet && details.holderPostalCode && {
  holderCity: details.holderCity,
  holderStreet: details.holderStreet,
  holderPostalCode: details.holderPostalCode,
}),
    } as PaymentDetails;

    // Add custom handling for NGN if needed
    if (selectedCurrency.code === "NGN") {
      builtDetails.providerName = details.providerName;
    }
  } else if (details.paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY) {
    builtDetails = {
      currency: details.currency,
      iso2: details.iso2,
      network: details.network,
      paymentMethod: details.paymentMethod,
      walletAddress: details.walletAddress,
    } as PaymentDetails;
  } else if (EMAIL_PAYMENT_METHODS.has(details.paymentMethod)) {
    builtDetails = {
      accountName: details.accountName,
      email: details.email,
      paymentMethod: details.paymentMethod,
      currency: selectedCurrency.code,
      iso2: selectedCurrency.iso2,
    } as PaymentDetails;
  } else {

    builtDetails = {
      ...baseDetails,
      paymentMethod: details.paymentMethod,
      providerName: details.providerName,
    } as PaymentDetails;
  }

  if (!validateBusinessAccountName(details.accountName || "", paymentData?.payerDetails.name || "")) {
    setAccountNameError("Account name must match your business name.");
  } else {
    setAccountNameError("");
  }

  setPaymentDetails(builtDetails);
  
   if (EMAIL_PAYMENT_METHODS.has(details.paymentMethod)) {
    setSendTo(details.email || details.accountName!);
  } else if (details.paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY) {
    setSendTo(truncateAddress(details.walletAddress!));
  } else if (
    details.paymentMethod === PAYMENT_METHOD.WECHAT_PAY ||
    details.paymentMethod === PAYMENT_METHOD.ALIPAY
  ) {
    setSendTo(`${details.accountName} (${details.phoneNumber})`);
  } else {
    setSendTo(details.accountName!);
  }
  
  setShowPayModal(false);
};

  // ---- Handle create order
  const handleCreateOrder = async () => {
    if (!paymentRequestToken) {
      setPayInitError("Missing payment token.");
      return;
    }

    setIsPayInitLoading(true);
    setPayInitError(null);

    let creditDetails = {};

    if (paymentData?.paymentDetails) {
      const { _id, ...details } = paymentData.paymentDetails;
      if (!details.iso2) {
        details.iso2 = paymentData.currencyIso2
        creditDetails = details;
      } else {
        creditDetails = paymentData.paymentDetails;
      }
    }

    if (orderAmount === null) {
      throw new Error("Order amount is null");
    }

    const buyerCreditDetails = buildPaymentDetails(creditDetails);
    const buyerDebitDetails = buildPaymentDetails(paymentDetails);

    // Build Order Data
    const orderData = {
      amount: orderAmount,
      buyerCreditPaymentMethod: paymentData?.paymentDetails?.paymentMethod,
      buyerCreditPaymentDetails: buyerCreditDetails,
      buyerDebitPaymentMethod: paymentDetails?.paymentMethod,
      buyerDebitPaymentDetails: buyerDebitDetails,
    };

    try {
      const idempotencyKey = `PAYLINKORDER::${paymentData?._id}-${fromCurrency}-${toCurrency}-${calculatedAmount}`;

      const response = await request.post({
        url: `/offer/${bdcOfferId}/payment-request/order?token=${paymentRequestToken}`,
        data: orderData,
        auth: false,
        headers: {
          idempotencykey: idempotencyKey,
          "Content-Type": "application/json",
        },
      });

      if (response.success) {
        const orderData = response.data as OrderResponse;
        setOrderId(orderData._id);
        setStep(4);
        setIsPayInitOpen(false);
      }
    } catch (error) {
      console.error("Error creating exchange order:", error);
      setPayInitError("Failed to create exchange order. Please try again.");
      setTimeout(() => {
        setPayInitError(null);
      }, 5000);
    } finally {
      setIsPayInitLoading(false);
    }
  };

  // ---- Handle Decline Payment Request.... working
  const handleDeclinePayment = async () => {
    if (!declineReason.trim()) {
      setDeclineError("Please provide a reason for the decline.");
      return;
    }

    if (!paymentRequestToken) {
      setDeclineError("Missing payment token.");
      return;
    }

    setIsDeclining(true);
    setDeclineError("");
    setDeclineSuccess(false);

    try {
      const response = await request.post({
        url: `/payment-link/decline?token=${paymentRequestToken}`,
        data: { reason: declineReason },
        auth: false,
      });

      if (response.success) {
        setDeclineError("");
        setDeclineSuccess(true);
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    } catch (error) {
      console.error("Error declining payment:", error);
      setDeclineError("Failed to decline payment. Please try again.");
      setTimeout(() => {
        setDeclineError("");
      }, 5000);
    } finally {
      setIsDeclining(false);
    }
  };

  // ---- handle buyer made payment
  const handleConfirmPayment = async () => {
    if (!paymentRequestToken) {
      setBuyerPaidError("Missing payment token.");
      return;
    }

    setIsBuyerPaidLoading(true);
    setBuyerPaidError(null);
    setBuyerPaid("");

    try {
      let buyerPaymentProofUrl: string | undefined;

      // Handle proof upload if needed
      if (
        (selectedExchangePaymentDetails?.paymentMethod === "MOBILE_MONEY" ||
          orderDetails?.sellerCreditPaymentDetails?.paymentMethod ===
            "MOBILE_MONEY") &&
        paymentProof
      ) {
        const presignedRes = await request.get({
          url: "/file-manager/file-upload-link",
          params: {
            filename: paymentProof.name,
            purpose: "PAYMENT_PROOF",
          },
          auth: false,
        });

        const presignData = presignedRes.data as preSignRes;

        const uploadLink = presignData?.uploadLink;
        const Key = presignData?.Key;

        if (!uploadLink || !Key) {
          throw new Error("Failed to get upload link for payment proof.");
        }

        const uploadRes = await fetch(uploadLink, {
          method: "PUT",
          body: paymentProof,
          headers: {
            "Content-Type": paymentProof.type,
          },
        });

        if (!uploadRes.ok) {
          throw new Error("Failed to upload payment proof.");
        }

        buyerPaymentProofUrl = encodeURI(
          Key.startsWith("/") ? Key.slice(1) : Key
        );
      }

      const dataPayload = buyerPaymentProofUrl ? { buyerPaymentProofUrl } : {};

      const idempotencyKey = `PAYLINK-BUYERPAID::${paymentData?._id}::ORDERID::${orderId}-${fromCurrency}-${toCurrency}-${calculatedAmount}`;

      const response = await request.post({
        url: `/order/${orderId}/payment-request/buyer-paid?token=${paymentRequestToken}`,
        data: dataPayload,
        headers: {
          idempotencykey: idempotencyKey,
          "Content-Type": "application/json",
        },
        auth: false,
      });

      if (response.success) {
        setBuyerPaid("Payment submitted successfully.");
        setTimeout(() => {
          closeModal();
          window.location.reload();
        }, 3000);
      }
    } catch (error) {
      console.error("Error confirming payment:", error);
      setBuyerPaidError("Failed to confirm payment. Please try again.");
      setTimeout(() => {
        setBuyerPaidError(null);
      }, 5000);
    } finally {
      setIsBuyerPaidLoading(false);
    }
  };

  // ---- get bank label
  const getProviderLabel = (paymentMethod?: string, label?: string) => {
    if (paymentMethod === "BANK_TRANSFER") {
      if (label === "Routing No.") return "Routing No";
      if (label === "Sort code") return "Sort Code";
      if (label === "IBAN") return "IBAN";
      return "Bank Name";
    }

    if (paymentMethod === "MOBILE_MONEY" || paymentMethod === "USSD") {
      return "Network";
    }

    return "Provider";
  };

  function roundUpWithDecimalJS(value: number, rate: number): string {
    const amount = new Decimal(value).div(rate);
    const rounded = amount.mul(100).ceil().div(100).toNumber();

    return rounded.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const getAccountNameLabel = (
    paymentMethod?: string,
    isUssdCode?: boolean
  ) => {
    if (isUssdCode) return "Account Name";
    if (paymentMethod === "MOBILE_MONEY") return "MoMo Name";
    return "Account Name";
  };

  const getAccountNumberLabel = (
    paymentMethod?: string,
    isUssdCode?: boolean
  ) => {
    if (isUssdCode) return "USSD Code";
    if (paymentMethod === "MOBILE_MONEY") return "MoMo Number";
    return "Account Number";
  };

  const renderblockchainName = () => {
    const network =
      selectedExchangePaymentDetails?.network ??
      orderDetails?.sellerCreditPaymentDetails?.network;

    return getBlockchainName(network);
  };

  const handlePayFromClick = () => {
      setMethodTypeContext("PAY");
      setShowMethodTypeModal(true);
  };

  const handleMethodTypeSelect = ( methodType: selectMethodTypes) => {
    setShowMethodTypeModal(false);

      if (methodTypeContext === "PAY") {
        setShowPayModal(true);
        setPaymentMethod(methodType);
      }
  };


  // ---- check if payment method is mobile money
  const isMobileMoney = orderDetails?.sellerCreditPaymentDetails?.paymentMethod ===
        PAYMENT_METHOD.MOBILE_MONEY || selectedExchangePaymentDetails?.paymentMethod === PAYMENT_METHOD.MOBILE_MONEY;

  if (loading || loadingCurrencies) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner className="h-10 w-10 text-blue-500" />
      </div>
    );
  }

  if (error)
    return (
      <div className="p-4 text-center ">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Something went wrong
        </h2>
        <p className="text-gray-700 dark:text-gray-300">{error}</p>
      </div>
    );

  if (!paymentData && !isExpired)
    return <div className="p-4">No data found.</div>;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow">
        <div className="mt-1 flex justify-center">
          <img
            className="dark:hidden"
            width={200}
            height={80}
            src="/images/logo/Primary Logo PNG crop1.png"
            alt="Primary Logo for light mode"
          />
          <img
            width={200}
            height={80}
            className="hidden dark:block"
            src="/images/logo/Primary Logo PNG_White new.png"
            alt="Logo for dark mode"
          />
        </div>

        {step < 5 && (
          <div className="max-w-xl mx-auto p-6 bg-[#F8F7FD] dark:bg-gray-800 shadow-md rounded-lg mt-1 transition-colors duration-300">
            {/* Step 1: Initial view */}
            {step === 1 && (
              <div className="space-y-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Hi {paymentData?.payerDetails?.name}, you've been requested to
                  pay{" "}
                  <span className="text-blue-600 dark:text-blue-400">
                    {paymentData?.amount.toLocaleString()} {" "}
                    {paymentData?.currency.abbr === "CNGN" ? "cNGN" : paymentData?.currency.abbr}
                  </span>{" "}
                  by {user}
                </h1>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Description
                  </h2>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    {/* <label className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                Description:
              </label> */}
                    <p className="text-gray-600 dark:text-gray-300 italic">
                      {paymentData?.displayMessage}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Validity Period{" "}
                  </h2>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-gray-600 dark:text-gray-300">
                      {paymentData?.expiresAt
                        ? format(
                            new Date(paymentData?.expiresAt),
                            "MMMM do yyyy, h:mm a"
                          )
                        : `Valid for a month from the date of creation (${
                            paymentData?.createdAt
                              ? format(
                                  new Date(paymentData?.createdAt),
                                  "MMMM do yyyy, h:mm a"
                                )
                              : ""
                          }).`}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Payer Information
                  </h2>
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600 dark:text-gray-300">
                        Name:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {paymentData?.payerDetails?.name}
                      </span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600 dark:text-gray-300">
                        Email:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {paymentData?.payerDetails?.email}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleNext}
                  className="mt-6 w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-95"
                >
                  Continue to Payment
                </button>
              </div>
            )}

            {/* Step 2: Currency selection */}
            {step === 2 && (
              <div className="space-y-6">
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <ArrowLeft className="h-5 w-5 mr-1" />
                  Back
                </button>
                <div className="relative" ref={dropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Your Currency
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      className="w-full px-4 py-3 pr-10 text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-between hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
                      onClick={() =>{
                        setShowCurrencyDropdown(!showCurrencyDropdown);
                        if (!showCurrencyDropdown) setCurrencySearch("");
                      }}
                    >
                      {selectedCurrency ? (
                        <div className="flex items-center">
                          <img
                            src={selectedCurrency.flag}
                            alt={selectedCurrency.code}
                            className="w-6 h-6 mr-3 rounded-full"
                          />
                          <span>{selectedCurrency.name}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">
                          Select currency
                        </span>
                      )}
                      <ChevronDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-300" />
                    </button>

                    {showCurrencyDropdown && (
                      // <div className="absolute z-[100] mt-2 w-full bg-white dark:bg-gray-700 shadow-lg rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 dark:ring-gray-600 focus:outline-none max-h-60 overflow-auto">
                      <div className="absolute z-[100] mt-2 w-full bg-white dark:bg-gray-700 shadow-lg rounded-lg text-base ring-1 ring-black ring-opacity-5 dark:ring-gray-600 focus:outline-none">
                        <div className="p-2 border-b border-gray-200 dark:border-gray-700">
      <input
        type="text"
        autoFocus
        placeholder="Search currency..."
        value={currencySearch}
        onChange={(e) => setCurrencySearch(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
                         <div className="max-h-60 overflow-auto py-1">
    {(() => {
        const search = currencySearch.trim().toLowerCase();
  console.log("search term:", search);         // ADD
  console.log("all currencies:", currencies);  // ADD
  // const search = currencySearch.trim().toLowerCase();
  const filtered = currencies.filter(
    (c) =>
      !search ||
      c.name.toLowerCase().includes(search) ||
      c.code.toLowerCase().includes(search)
  );
    console.log("filtered:", filtered);          // ADD
  return filtered.length === 0 ? (
    <p className="px-4 py-2 text-sm text-gray-400">No results</p>
  ) : (
    filtered.map((currency) => (
      <div
        key={`${currency.iso2}-${currency.code}`}
        className={`flex items-center px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors ${
          selectedCurrency?.code === currency.code
            ? "bg-gray-100 dark:bg-gray-600"
            : ""
        }`}
        onClick={() => handleSelectCurrency(currency)}
      >
        <img src={currency.flag} alt={currency.code} className="w-5 h-5 mr-3 rounded-full" />
        <span className="text-gray-900 dark:text-white">{currency.name}</span>
        <span className="ml-auto text-xs text-gray-400">{currency.code}</span>
      </div>
    ))
  );
})()}
    </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Error message display */}
                {rateError && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                    <p className="text-red-600 dark:text-red-400">
                      {rateError}
                    </p>
                    <button
                      onClick={() => {
                        setRateError("");
                        setStep(1);
                      }}
                      className="mt-2 text-sm text-red-700 dark:text-red-300 hover:underline"
                    >
                      Go back and try again
                    </button>
                  </div>
                )}

                {isLoading && !rateError && (
                  <div className="flex justify-center items-center my-6 py-8">
                    <Spinner className="h-10 w-10 text-blue-500 dark:text-blue-400" />
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Payment details */}
            {step === 3 && exchangeRate?.paymentDetails && (
              <div className="space-y-6">
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <ArrowLeft className="h-5 w-5 mr-1" />
                  Back
                </button>

                {displayAmount !== null && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg mb-4">
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        You are going to pay:{" "}
                        <span className="text-blue-600 dark:text-blue-400">
                          {displayAmount} {" "}
                          {exchangeRate.fromCurrency.abbr === "CNGN" ? "cNGN" : exchangeRate.fromCurrency.abbr}
                        </span>
                      </p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        The recipient will receive:{" "}
                        <span className="text-green-600 dark:text-green-400">
                          {paymentData?.amount.toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })} {" "}
                          {paymentData?.currency.abbr}
                        </span>
                      </p>
                    </div>
                    <div className="mb-6 flex items-start gap-3 rounded-lg bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
                      <TriangleAlert size={20} color="red" strokeWidth={3} />
                      <p>
                        Please note: the exchange rate is subject to change
                        until payment is made. Please refresh the page to get
                        the latest exchange rate.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                        Input Account sending from
                      </label>
                      {accountNameError && (
                        <div className="p-2 mb-2 text-red-700 bg-red-100 border border-red-400 rounded-md text-sm">
                          {accountNameError}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={handlePayFromClick}
                        disabled={!selectedCurrency}
                        className={`w-full px-4 py-3 text-left text-gray-700 bg-gray-50 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white ${
                          !selectedCurrency
                            ? "cursor-not-allowed opacity-50"
                            : "cursor-pointer hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:hover:border-blue-500"
                        }`}
                      >
                        {sendTo || "Select sending methods"}
                        <ChevronDownIcon className="float-right h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleOpenDeclineModal}
                    className="flex-1 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 font-medium py-3 px-6 rounded-lg transition-all duration-200 border border-red-200 dark:border-red-800"
                  >
                    Decline
                  </button>
                  <button
                    onClick={handleOpenPayInitModal}
                    disabled={!sendTo || !!accountNameError}
                    className={`flex-1 text-white font-medium py-3 px-6 rounded-lg 
    ${
      !sendTo || !!accountNameError
        ? "bg-blue-400 cursor-not-allowed"
        : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600"
    }`}
                  >
                    Continue to Payment
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Payment confirmation */}
            {step === 4 && (
              <div className="space-y-6">
                {orderDetails ? (
                  // ✅ SHOW WHEN orderDetails IS AVAILABLE
                  <>
                    <div className="mb-6 flex items-start gap-3 rounded-lg bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
                      <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <p>
                        The details below are linked to your existing exchange
                        order. Proceed with payment to complete the transaction.
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      {uploadError && (
                        <div className="mb-4">
                          <Alert
                            variant="error"
                            title="Upload Error"
                            message={uploadError}
                            showLink={false}
                          />
                        </div>
                      )}
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg mb-4">
                          <p className="text-lg font-semibold text-gray-900 dark:text-white">
                            {orderDetails.buyerMadePayment
                              ? "You Paid "
                              : "Initiated payment of "}
                            <span className="text-blue-600 dark:text-blue-400">
                              {orderDetails.sellerCreditPaymentDetails
                                ?.isUssdCode
                                ? ussdAmount
                                : roundUpWithDecimalJS(
                                  Number(orderDetails.value),
                                  Number(orderDetails.rate)
                                )} {" "}
                                {orderDetails.fromCurrency.abbr === "CNGN" ? "cNGN" : orderDetails.fromCurrency.abbr}
                            </span>
                          </p>
                        </div>
                      </div>

                      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                        Recipient Details
                      </h2>

                      {orderDetails.sellerCreditPaymentDetails.paymentMethod ===
                      PAYMENT_METHOD.STABLE_CURRENCY ? (
                        <>
                          <div className="space-y-1">
                            {/* Label */}
                            <span className="block text-gray-600 dark:text-gray-300">
                              Wallet Address:
                            </span>

                            {/* Address + Copy */}
                            <div className="flex items-start gap-2 max-w-full">
                              <p className="text-gray-800 dark:text-white break-all whitespace-normal flex-1 max-w-full">
                                {
                                  orderDetails.sellerCreditPaymentDetails
                                    .walletAddress
                                }
                              </p>
                              <CopyButton
                                text={
                                  orderDetails.sellerCreditPaymentDetails
                                    .walletAddress!
                                }
                              />
                            </div>
                          </div>

                          <div className="flex justify-between mt-3">
                            <span className="text-gray-600 dark:text-gray-300">
                              Network:
                            </span>
                            <span className="text-gray-800 dark:text-white">
                              {renderblockchainName()}
                            </span>
                          </div>
                        </>
                        ) : orderDetails.sellerCreditPaymentDetails.paymentMethod === PAYMENT_METHOD.WECHAT_PAY ||
  orderDetails.sellerCreditPaymentDetails.paymentMethod === PAYMENT_METHOD.ALIPAY ? (
  <div className="space-y-3">
    <div className="flex justify-between">
      <span className="text-gray-600 dark:text-gray-300">Account Name:</span>
      <span className="font-medium text-gray-900 dark:text-white">
        {orderDetails.sellerCreditPaymentDetails?.accountName}
      </span>
    </div>
    <div className="flex justify-between">
      <span className="text-gray-600 dark:text-gray-300">Phone Number:</span>
      <span className="font-medium text-gray-900 dark:text-white">
        {orderDetails.sellerCreditPaymentDetails?.phoneNumber}
      </span>
    </div>
  </div>
                      ) :  EMAIL_PAYMENT_METHODS.has(
                          orderDetails.sellerCreditPaymentDetails.paymentMethod
                        ) ? (
                        // ✅ NEW: Email payment methods (Zelle, Interac)
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">
                              Account Name:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {
                                orderDetails.sellerCreditPaymentDetails
                                  ?.accountName
                              }
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">
                              Email Address:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {orderDetails.sellerCreditPaymentDetails?.email}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">
                              Payment Method:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {
                                orderDetails.sellerCreditPaymentDetails
                                  .paymentMethod
                              }
                            </span>
                          </div>
                        </div>
                      ) : (
                        // ✅ EXISTING: Bank Transfer, Mobile Money, etc.
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">
                              {getAccountNameLabel(
                                orderDetails.sellerCreditPaymentDetails
                                  ?.paymentMethod,
                                orderDetails.sellerCreditPaymentDetails
                                  ?.isUssdCode
                              )}
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {
                                orderDetails.sellerCreditPaymentDetails
                                  ?.accountName
                              }
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">
                              {getAccountNumberLabel(
                                orderDetails.sellerCreditPaymentDetails
                                  ?.paymentMethod,
                                orderDetails.sellerCreditPaymentDetails
                                  ?.isUssdCode
                              )}
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {orderDetails.sellerCreditPaymentDetails
                                ?.isUssdCode
                                ? `${orderDetails.sellerCreditPaymentDetails.accountNumber}${ussdAmount}#`
                                : orderDetails.sellerCreditPaymentDetails
                                    ?.accountNumber}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            {/* <span className="text-gray-600 dark:text-gray-300">
                              {getProviderLabel(
                                orderDetails.sellerCreditPaymentDetails
                                  .paymentMethod,
                                orderDetails.sellerCreditPaymentDetails.currency
                                  .bankCodeLabel
                              )}
                            </span> */}
                            <span className="font-medium text-gray-900 dark:text-white">
  {["Sort code", "IBAN", "Routing No."].includes(
    orderDetails.sellerCreditPaymentDetails.currency.bankCodeLabel ?? ""
  )
    ? orderDetails.sellerCreditPaymentDetails?.bankCode
    : orderDetails.sellerCreditPaymentDetails?.providerName}
</span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {
                                orderDetails.sellerCreditPaymentDetails
                                  .providerName
                              }
                            </span>
                          </div>
                          {(orderDetails.fromCurrency.abbr === "GBP" || orderDetails.fromCurrency.abbr === "EUR") &&
  orderDetails.sellerCreditPaymentDetails?.providerName &&
  orderDetails.sellerCreditPaymentDetails?.providerName !== orderDetails.sellerCreditPaymentDetails?.bankCode && (
  <div className="flex justify-between">
    <span className="text-gray-600 dark:text-gray-300">Bank Name</span>
    <span className="font-medium text-gray-900 dark:text-white">
      {orderDetails.sellerCreditPaymentDetails?.providerName}
    </span>
  </div>
)}
                          {orderDetails.sellerCreditPaymentDetails?.holderCity &&
  orderDetails.sellerCreditPaymentDetails?.holderStreet &&
  orderDetails.sellerCreditPaymentDetails?.holderPostalCode && (
  <div className="flex justify-between">
    <span className="text-gray-600 dark:text-gray-300">Holder Address:</span>
    <span className="font-medium text-gray-900 dark:text-white text-right">
      {`${orderDetails.sellerCreditPaymentDetails.holderStreet}, ${orderDetails.sellerCreditPaymentDetails.holderCity}, ${orderDetails.sellerCreditPaymentDetails.holderPostalCode}`}
    </span>
  </div>
)}
                        </div>
                      )}

                      {orderDetails.sellerCreditPaymentDetails
                        ?.paymentMethod === PAYMENT_METHOD.MOBILE_MONEY &&
                        !orderDetails.buyerMadePayment && (
                          <div className="mt-6">
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                              Please upload proof of payment
                            </p>
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileChange}
                              accept="image/*,.pdf"
                              className="hidden"
                            />
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 dark:border-gray-600"
                            >
                              {paymentProof ? paymentProof.name : "Select File"}
                            </button>
                            {paymentProof && (
                              <p className="mt-1 text-sm text-green-600">
                                File selected
                              </p>
                            )}
                          </div>
                        )}
                    </div>
                  </>
                ) : exchangeRate ? (
                  // ✅ SHOW WHEN exchangeRate IS AVAILABLE
                  <>
                    {selectedExchangePaymentDetails?.isUssdCode ? (
                      <div className="mb-6 flex items-start gap-3 rounded-lg bg-blue-50 p-4 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                        <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <p>
                          The USSD code below belongs to Monirates and/or its Partner. Your Payment is safe and secure. Please dial the code to complete your payment and upload proof of payment.
                        </p>
                      </div>
                    ) : selectedExchangePaymentDetails?.paymentMethod ===
                      PAYMENT_METHOD.STABLE_CURRENCY ? (
                      <div className="mb-6 flex items-start gap-3 rounded-lg bg-blue-50 p-4 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                        <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <p>
                          The wallet address below belongs to Monirates and/or its Partner. Your Payment is safe and secure. Please transfer the amount to the account.
                        </p>
                      </div>
                    ) : EMAIL_PAYMENT_METHODS.has(
                        selectedExchangePaymentDetails?.paymentMethod
                      ) ? (
                      // ✅ NEW: Email payment methods (Zelle, Interac)
                      <div className="mb-6 flex items-start gap-3 rounded-lg bg-blue-50 p-4 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                        <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <p>
                          The email details below belongs to Monirates and/or its Partner. Your Payment is safe and secure. Please send the payment to the email address provided.
                        </p>
                      </div>
                    ) : (
                      <div className="mb-6 flex items-start gap-3 rounded-lg bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
                        <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <p>
                          The account details below belongs to Monirates and/or its Partner. Your Payment is safe and secure. Please transfer the amount to the account.
                        </p>
                      </div>
                    )}

                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg mb-4">
                      {uploadError && (
                        <div className="mb-4">
                          <Alert
                            variant="error"
                            title="Upload Error"
                            message={uploadError}
                            showLink={false}
                          />
                        </div>
                      )}
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        Payment of{" "}
                        <span className="text-green-600 dark:text-green-400">
                          {displayAmount?.toLocaleString()} {" "}
                          {exchangeRate.fromCurrency.abbr === "CNGN" ? "cNGN" : exchangeRate.fromCurrency.abbr}
                        </span>{" "}
                        initiated
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                        Recipient Details
                      </h2>

                      {selectedExchangePaymentDetails?.paymentMethod ===
                      PAYMENT_METHOD.STABLE_CURRENCY ? (
                        <>
                          <div className="space-y-1">
                            {/* Label */}
                            <span className="block text-gray-600 dark:text-gray-300">
                              Wallet Address:
                            </span>

                            {/* Address + Copy */}
                            <div className="flex items-start gap-2 max-w-full">
                              <p className="text-gray-800 dark:text-white break-all whitespace-normal flex-1 max-w-full">
                                {selectedExchangePaymentDetails?.walletAddress}
                              </p>
                              <CopyButton
                                text={
                                  selectedExchangePaymentDetails?.walletAddress!
                                }
                              />
                            </div>
                          </div>

                          <div className="flex justify-between mt-3">
                            <span className="text-gray-600 dark:text-gray-300">
                              Network:
                            </span>
                            <span className="text-gray-800 dark:text-white">
                              {renderblockchainName()}
                            </span>
                          </div>
                        </>
                                              ) : selectedExchangePaymentDetails?.paymentMethod === PAYMENT_METHOD.WECHAT_PAY ||
  selectedExchangePaymentDetails?.paymentMethod === PAYMENT_METHOD.ALIPAY ? (
  <div className="space-y-3">
    <div className="flex justify-between">
      <span className="text-gray-600 dark:text-gray-300">Account Name:</span>
      <span className="font-medium text-gray-900 dark:text-white">
        {selectedExchangePaymentDetails?.accountName}
      </span>
    </div>
    <div className="flex justify-between">
      <span className="text-gray-600 dark:text-gray-300">Phone Number:</span>
      <span className="font-medium text-gray-900 dark:text-white">
        {selectedExchangePaymentDetails?.phoneNumber}
      </span>
    </div>
  </div>
                      ) : EMAIL_PAYMENT_METHODS.has(
                          selectedExchangePaymentDetails?.paymentMethod
                        ) ? (
                        // ✅ NEW: Email payment methods (Zelle, Interac)
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">
                              Account Name:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {selectedExchangePaymentDetails?.accountName}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">
                              Email Address:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {selectedExchangePaymentDetails?.email}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">
                              Payment Method:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {selectedExchangePaymentDetails?.paymentMethod}
                            </span>
                          </div>
                        </div>
                      ) : (
                        // ✅ EXISTING: Bank Transfer, Mobile Money, etc.
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">
                              {getAccountNameLabel(
                                selectedExchangePaymentDetails?.paymentMethod,
                                selectedExchangePaymentDetails?.isUssdCode
                              )}
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {selectedExchangePaymentDetails?.accountName}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">
                              {getAccountNumberLabel(
                                selectedExchangePaymentDetails?.paymentMethod,
                                selectedExchangePaymentDetails?.isUssdCode
                              )}
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {selectedExchangePaymentDetails?.isUssdCode
                                ? `${selectedExchangePaymentDetails?.accountNumber}${ussdAmountForSelected}#`
                                : selectedExchangePaymentDetails?.accountNumber}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">
                              {getProviderLabel(
                                selectedExchangePaymentDetails?.paymentMethod,
                                selectedExchangePaymentDetails?.currency
                                  .bankCodeLabel
                              )}
                            </span>
                            {/* <span className="font-medium text-gray-900 dark:text-white">
                              {selectedExchangePaymentDetails?.providerName}
                            </span> */}
                            <span className="font-medium text-gray-900 dark:text-white">
  {["Sort code", "IBAN", "Routing No."].includes(
    selectedExchangePaymentDetails?.currency.bankCodeLabel ?? ""
  )
    ? selectedExchangePaymentDetails?.bankCode
    : selectedExchangePaymentDetails?.providerName}
</span>
                          </div>
                          {(exchangeRate?.fromCurrency.abbr === "GBP" || exchangeRate?.fromCurrency.abbr === "EUR") &&
  selectedExchangePaymentDetails?.providerName &&
  selectedExchangePaymentDetails?.providerName !== selectedExchangePaymentDetails?.bankCode && (
  <div className="flex justify-between">
    <span className="text-gray-600 dark:text-gray-300">Bank Name</span>
    <span className="font-medium text-gray-900 dark:text-white">
      {selectedExchangePaymentDetails?.providerName}
    </span>
  </div>
)}
                          {selectedExchangePaymentDetails?.holderCity &&
  selectedExchangePaymentDetails?.holderStreet &&
  selectedExchangePaymentDetails?.holderPostalCode && (
  <div className="flex justify-between">
    <span className="text-gray-600 dark:text-gray-300">Holder Address:</span>
    <span className="font-medium text-gray-900 dark:text-white text-right">
      {`${selectedExchangePaymentDetails.holderStreet}, ${selectedExchangePaymentDetails.holderCity}, ${selectedExchangePaymentDetails.holderPostalCode}`}
    </span>
  </div>
)}
                          {/* Upload proof of payment  */}
                          {selectedExchangePaymentDetails?.paymentMethod ===
                            PAYMENT_METHOD.MOBILE_MONEY && (
                            <div className="mt-6">
                              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                                Please upload proof of payment
                              </p>
                              <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*,.pdf"
                                className="hidden"
                              />
                              <button
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 dark:border-gray-600"
                              >
                                {paymentProof
                                  ? paymentProof.name
                                  : "Select File"}
                              </button>
                              {paymentProof && (
                                <p className="mt-1 text-sm text-green-600">
                                  File selected
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  // ❗Fallback if neither available
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    Loading payment details...
                  </div>
                )}

                <button
                  onClick={openModal}
                  disabled={
                    orderDetails?.buyerMadePayment ||
                    (isMobileMoney && !paymentProof)
                  }
                  className={`w-full text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform active:scale-95
    ${
      orderDetails?.buyerMadePayment || (isMobileMoney && !paymentProof)
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600"
    }`}
                >
                  {orderDetails?.buyerMadePayment
                    ? `Awaiting Confirmation of Payment`
                    : "Confirm Payment"}
                </button>
              </div>
            )}

            {/* initialize order  */}
            <Modal
              isOpen={isPayInitOpen}
              onClose={closePayInitModal}
              className="max-w-md"
            >
              <div className="p-6 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4">
                  <svg
                    className="h-6 w-6 text-blue-600 dark:text-blue-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M12 20.5C6.753 20.5 2.5 16.247 2.5 11S6.753 1.5 12 1.5 21.5 5.753 21.5 11 17.247 20.5 12 20.5z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Confirm Payment
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Are you sure you want to proceed with a payment of{" "}
                  <span className="font-semibold">
                    {displayAmount} {" "}
                    {exchangeRate?.fromCurrency.abbr === "CNGN" ? "cNGN" : exchangeRate?.fromCurrency.abbr}
                  </span>
                  ? This action is irreversible.
                </p>

                {payInitError && (
                  <p className="mb-4 text-sm text-red-500">{payInitError}</p>
                )}

                <div className="flex justify-center gap-4">
                  <button
                    onClick={closePayInitModal}
                    disabled={isPayInitLoading}
                    className="px-6 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition-colors"
                  >
                    No, Not Now.
                  </button>
                  <button
                    onClick={handleCreateOrder}
                    disabled={isPayInitLoading}
                    className={`px-6 py-2 rounded-lg transition-colors text-white ${
                      isPayInitLoading
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {isPayInitLoading ? "Processing..." : "Yes, Proceed"}
                  </button>
                </div>
              </div>
            </Modal>

            {/* Confirmation Modal for buyer made payment */}
            <Modal isOpen={isOpen} onClose={closeModal} className="max-w-md">
              <div className="p-6 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4">
                  <svg
                    className="h-6 w-6 text-blue-600 dark:text-blue-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Confirm Payment?
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Are you sure you've completed this payment of{" "}
                  <span className="font-semibold">
                    {exchangeRate?.fromCurrency.symbol}
                    {displayAmount}
                  </span>
                  ?
                </p>

                {buyerPaidError && (
                  <p className="mb-4 text-sm text-red-500">{buyerPaidError}</p>
                )}

                {buyerPaid && (
                  <p className="mb-4 text-sm text-green-600 dark:text-green-400 font-medium">
                    {buyerPaid}
                  </p>
                )}

                <div className="flex justify-center gap-4">
                  <button
                    onClick={closeModal}
                    className="px-6 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition-colors"
                  >
                    No, Go Back
                  </button>
                  <button
                    onClick={handleConfirmPayment}
                    disabled={isBuyerPaidLoading}
                    className={`px-6 py-2 rounded-lg text-white transition-colors ${
                      isBuyerPaidLoading
                        ? "bg-green-400 cursor-not-allowed"
                        : "bg-green-600 hover:bg-green-700"
                    }`}
                  >
                    {isBuyerPaidLoading ? "Confirming..." : "Yes, I have paid"}
                  </button>
                </div>
              </div>
            </Modal>

            {/* decline modal  */}
            <Modal
              isOpen={isDeclineOpen}
              onClose={closeDeclineModal}
              className="max-w-md"
            >
              <div className="p-6 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                  <svg
                    className="h-6 w-6 text-red-600 dark:text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Decline Payment?
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Are you sure you want to decline the payment of{" "}
                  <span className="font-semibold">
                    {exchangeRate?.fromCurrency.symbol}
                    {displayAmount}
                  </span>
                  ?
                </p>

                <div className="mb-4 text-left">
                  <label
                    htmlFor="decline-reason"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Reason for Decline
                  </label>
                  <input
                    id="decline-reason"
                    type="text"
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Enter reason"
                  />
                  {declineError && (
                    <p className="mt-1 text-sm text-red-500">{declineError}</p>
                  )}

                  {declineSuccess && (
                    <p className="mt-1 text-sm text-green-600 dark:text-green-400 font-medium">
                      Payment request has been declined successfully.
                    </p>
                  )}
                </div>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={closeDeclineModal}
                    className="px-6 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition-colors"
                    disabled={isDeclining}
                  >
                    No, Go Back
                  </button>
                  <button
                    onClick={handleDeclinePayment}
                    disabled={!declineReason.trim() || isDeclining}
                    className={`px-6 py-2 rounded-lg transition-colors text-white ${
                      isDeclining || !declineReason.trim()
                        ? "bg-red-400 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    {isDeclining ? "Declining..." : "Yes, Decline"}
                  </button>
                </div>
              </div>
            </Modal>

            <SelectMethodTypeModal
              isOpen={showMethodTypeModal}
              onClose={() => setShowMethodTypeModal(false)}
              onSelect={handleMethodTypeSelect}
              title={methodTypeContext === "PAY" ? "Send From" : "Receive To"}
              currency={
                methodTypeContext === "PAY"
                  ? selectedCurrency?.code || ""
                  : selectedCurrency?.code || ""
              }
              availableMethods={
                methodTypeContext === "PAY"
                  ? Array.isArray(exchangeRate?.acceptedPaymentMethod)
                    ? exchangeRate.acceptedPaymentMethod
                    : [exchangeRate?.acceptedPaymentMethod || ""]
                  : Array.isArray(exchangeRate?.payoutPaymentMethod)
                  ? exchangeRate.payoutPaymentMethod
                  : [exchangeRate?.payoutPaymentMethod || ""]
              }
            />

            {selectedCurrency && (
              <SendingPaymentDetailsModal
                isOpen={showPayModal}
                onClose={() => setShowPayModal(false)}
                onSave={handleSavePaymentDetails}
                currency={selectedCurrency.code}
                paymentMethod={paymentMethod}
                fromCurrency={{
                  code: selectedCurrency.code,
                  iso2: selectedCurrency.iso2,
                  name: selectedCurrency.name,
                  hasBankList: selectedCurrency?.hasBankList ?? false,
                  paymentMethod: selectedCurrency.paymentMethod,
                }}
                token={paymentRequestToken}
                exchangeRate={exchangeRate}
              />
            )}
          </div>
        )}
      </div>

      {/* Status Section - Only shows in step 5 */}
      {step === 5 &&
        (() => {
          const statusConfig = getStatusConfig(paymentData?.status, isExpired);
          return (
            <div
              className={`w-full md:w-[500px] mx-auto p-6 shadow-md rounded-lg mt-1 transition-colors duration-300 ${statusConfig.bg}`}
            >
              <div className="text-center space-y-4">
                <h2 className={`text-2xl font-bold ${statusConfig.titleColor}`}>
                  {statusConfig.title}
                </h2>
                <p className={statusConfig.messageColor}>
                  {statusConfig.message}
                </p>
              </div>
            </div>
          );
        })()}

      {/*CTA Section - Only shows in step 5 */}
      {step === 5 && (
        <>
          {/* <div className="flex justify-center mb-0 -mt-15">
            <img
              src="/images/logo/monirates cta logo.png"
              alt="Monirates - Send and Receive Payments Across Africa"
              className="max-w-full h-auto rounded-lg "
              width={300}
              height={50}
            />
          </div> */}
          <div className=" mx-full px-6 mt-5">
            <div className="bg-white dark:bg-gray-800 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 shadow-[0_1px_10px_0_#00000026]">
              {/* Individual Section - App Stores */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center px-4 py-2 rounded-lg mb-4 bg-gradient-to-r from-[#794BD8] to-[#F0598D]">
                  <span className="text-sm font-medium text-white">
                    For Individuals
                  </span>
                </div>

                <p className="text-dark dark:text-white text-center mb-8 text-lg">
                  Create your account and receive payments across Africa with
                  competitive rates.
                </p>

                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <a
                    href="https://apps.apple.com/us/app/monirates/id6473445696"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex justify-center"
                  >
                    <div className="w-[180px] h-[50px] flex items-center justify-center">
                      <img
                        src="/images/logo/Download_on_the_App_Store_RGB_blk.svg"
                        alt="Download on the App Store"
                        className="object-contain w-full h-full hover:scale-105 transition-transform"
                      />
                    </div>
                  </a>

                  <a
                    href="https://play.google.com/store/apps/details?id=com.monirates"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex justify-center"
                  >
                    <div className="w-[180px] h-[50px] flex items-center justify-center">
                      <img
                        src="/images/logo/Google_Play_Store_badge_EN.svg"
                        alt="Get it on Google Play"
                        className="object-contain w-full h-full hover:scale-105 transition-transform"
                      />
                    </div>
                  </a>
                </div>
              </div>

              {/* Business Section - Sign Up */}
              <div className="text-center mb-3">
                <div className="inline-flex items-center justify-center px-4 py-2 rounded-lg mb-4 bg-gradient-to-r from-[#794BD8] to-[#F0598D]">
                  <span className="text-sm font-medium text-white">
                    For Businesses
                  </span>
                </div>

                <p className="text-dark dark:text-white text-center mb-8 text-lg">
                  Cross-border payments, global payouts, and off-ramping for
                  your business. Instant settlement. Best rates.
                </p>
              </div>

              <div className="text-center pt-1">
                <a
                  href={`${import.meta.env.VITE_PAYLINK_BASE_URL}/signup`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-4 bg-gradient-to-r from-[#DDEEFB] to-[#B8AEEC] text-[#3126A5] font-semibold rounded-[70px] transition-all duration-200 transform hover:scale-105 hover:shadow-lg text-lg"
                >
                  Sign Up Now
                </a>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Footer  */}
      <div className="mt-5 p-6 bg-gray-50 dark:bg-gray-800 text-center text-gray-900 dark:text-white shadow-lg">
        <p>
          Copyright &copy; {new Date().getFullYear()} Monirates - All Rights
          Reserved | No border. No limit.
        </p>
      </div>
    </div>
  );
}

export default PaymentLinkPay;
